import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Sparkles, Wand2, Loader2, Music2 } from 'lucide-react';
import { GENRES, MOODS, INSTRUMENTS, LYRICS_TEMPLATES } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import SongCard from '../components/SongCard';
import { useNavigate } from 'react-router-dom';
import GenerationProgress from '../components/GenerationProgress';

export default function Create() {
  const { t, lang, user, api, library, refreshLibrary, playSong } = useApp();
  const { toast } = useToast();
  const nav = useNavigate();
  const [mode, setMode] = useState('custom');
  const [desc, setDesc] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [styles, setStyles] = useState(['Dancehall']); // multi-select genres
  const [titleField, setTitleField] = useState('');
  const [instrumental, setInstrumental] = useState(false);
  const [weirdness, setWeirdness] = useState([35]);
  const [styleStrength, setStyleStrength] = useState([60]);
  const [generating, setGenerating] = useState(false);
  const [genLyrics, setGenLyrics] = useState(false);

  // Derived primary style label (comma-joined) for API + display
  const styleLabel = styles.join(', ');
  const primaryStyle = styles[0] || 'Pop';

  const toggleStyle = (g) => {
    setStyles(prev => {
      if (prev.includes(g)) {
        const next = prev.filter(x => x !== g);
        return next.length === 0 ? [g] : next; // keep at least one selected
      }
      if (prev.length >= 3) return [...prev.slice(1), g]; // max 3
      return [...prev, g];
    });
  };

  const generateLyrics = async () => {
    setGenLyrics(true);
    try {
      const data = await api.genLyrics({ prompt: desc, genre: styleLabel, language: lang, instrumental });
      setLyrics(data.lyrics || '');
      toast({ title: lang==='fr'?'Paroles générées par l\u2019IA':'Lyrics generated by AI' });
    } catch (e) {
      const tpl = LYRICS_TEMPLATES[primaryStyle] || LYRICS_TEMPLATES.Rap;
      setLyrics(tpl);
      toast({ title: lang==='fr'?'IA indisponible — modèle local utilisé':'AI unavailable — local template used' });
    } finally { setGenLyrics(false); }
  };

  const generateSong = async () => {
    if (!desc && !lyrics && mode==='simple') {
      toast({ title: lang==='fr'?'Ajoute une description':'Add a description' });
      return;
    }
    if (!user) {
      toast({ title: lang==='fr'?'Connecte-toi pour sauvegarder':'Sign in to save your songs' });
      nav('/auth?mode=signin');
      return;
    }
    setGenerating(true);
    try {
      const data = await api.createSong({
        title: titleField,
        genre: styleLabel,
        prompt: desc,
        lyrics,
        instrumental,
        language: lang,
      });
      await refreshLibrary();
      const isPending = data?.song?.sunoStatus === 'pending';
      toast({
        title: isPending
          ? (lang==='fr'?'Génération lancée 🎵':'Generation started 🎵')
          : (lang==='fr'?'Chanson générée':'Song generated'),
        description: isPending
          ? (lang==='fr'?'Suno IA compose ta chanson (2-3 min). Tu peux quitter cette page, ta chanson apparaîtra dans la Bibliothèque.':'Suno AI is composing your song (2-3 min). You can leave this page; it will appear in your Library.')
          : (lang==='fr'?'Ajoutée à ta bibliothèque':'Added to your library')
      });
      if (data?.song && !isPending) playSong(data.song, [data.song]);
    } catch (e) {
      toast({ title: lang==='fr'?'Échec de la création':'Creation failed', description: e?.response?.data?.detail || String(e) });
    } finally { setGenerating(false); }
  };

  const recents = useMemo(()=>library.slice(0,6), [library]);

  return (
    <div className="px-4 lg:px-8 py-6 fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold">{t('create.title')}</h1>
          <p className="text-zinc-400 text-sm mt-1">{t('create.subtitle')}</p>
        </div>
        <div className="pill text-zinc-300"><Sparkles size={12} className="text-yellow-300" /> {t('create.tip')}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Form */}
        <div className="lg:col-span-3 space-y-5 bg-[#101012] border border-white/5 rounded-2xl p-5">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="bg-[#16161a] border border-white/5">
              <TabsTrigger value="simple">{t('create.simple')}</TabsTrigger>
              <TabsTrigger value="custom">{t('create.custom')}</TabsTrigger>
            </TabsList>

            <TabsContent value="simple" className="mt-5 space-y-4">
              <div>
                <Label className="text-zinc-300">{t('create.song_desc')}</Label>
                <Textarea value={desc} onChange={(e)=>setDesc(e.target.value)} placeholder={t('create.song_desc_ph')} className="mt-2 min-h-28 bg-[#16161a] border-white/10" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Switch checked={instrumental} onCheckedChange={setInstrumental} /> <span className="text-sm">{t('create.instrumental')}</span></div>
              </div>
            </TabsContent>

            <TabsContent value="custom" className="mt-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">{t('create.title_field')}</Label>
                  <Input value={titleField} onChange={(e)=>setTitleField(e.target.value)} placeholder={t('create.title_ph')} className="mt-2 bg-[#16161a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300">{t('create.style')}</Label>
                  <Input
                    value={styleLabel}
                    onChange={(e)=>{
                      const list = e.target.value.split(',').map(s=>s.trim()).filter(Boolean);
                      setStyles(list.length ? list.slice(0,3) : ['Pop']);
                    }}
                    placeholder={t('create.style_ph')}
                    className="mt-2 bg-[#16161a] border-white/10"
                  />
                  <div className="text-[11px] text-zinc-500 mt-1">{lang==='fr'?'Combine jusqu\u2019\u00e0 3 styles, s\u00e9par\u00e9s par des virgules':'Combine up to 3 styles, comma-separated'}</div>
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">{t('create.genres')} <span className="text-[11px] text-zinc-500 font-normal">({styles.length}/3 {lang==='fr'?'s\u00e9lectionn\u00e9s':'selected'})</span></Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {GENRES.map(g => {
                    const selected = styles.includes(g);
                    return (
                      <button key={g} type="button" onClick={()=>toggleStyle(g)} className={`pill cursor-pointer ${selected?'bg-white text-black border-white':'hover:bg-white/10'}`}>{g}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">{t('create.moods')}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {MOODS.map(m => (
                    <button key={m} type="button" onClick={()=>setDesc(d => d ? d + ', ' + m : m)} className="pill hover:bg-white/10">{m}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">{t('create.instruments')}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {INSTRUMENTS.map(i => (
                    <button key={i} type="button" onClick={()=>setDesc(d => d ? d + ', ' + i : i)} className="pill hover:bg-white/10">{i}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">{t('create.lyrics')}</Label>
                  <Button size="sm" variant="outline" onClick={generateLyrics} disabled={genLyrics} className="border-white/15 bg-transparent hover:bg-white/5 gap-2">
                    {genLyrics ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                    {t('create.generate_lyrics')}
                  </Button>
                </div>
                <Textarea value={lyrics} onChange={(e)=>setLyrics(e.target.value)} placeholder={t('create.lyrics_ph')} className="mt-2 min-h-40 bg-[#16161a] border-white/10 font-mono text-sm" />
                <GenerationProgress active={genLyrics} kind="lyrics" lang={lang} estimate={22} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between text-sm text-zinc-300"><span>Weirdness</span><span className="text-zinc-500">{weirdness[0]}%</span></div>
                  <Slider value={weirdness} onValueChange={setWeirdness} max={100} step={1} className="mt-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm text-zinc-300"><span>Style strength</span><span className="text-zinc-500">{styleStrength[0]}%</span></div>
                  <Slider value={styleStrength} onValueChange={setStyleStrength} max={100} step={1} className="mt-2" />
                </div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={instrumental} onCheckedChange={setInstrumental} /> <span className="text-sm">{t('create.instrumental')}</span></div>
            </TabsContent>
          </Tabs>

          <div className="pt-3">
            <Button onClick={generateSong} disabled={generating} className="w-full bg-white text-black hover:bg-zinc-200 h-12 text-base gap-2">
              {generating ? <Loader2 className="animate-spin" size={18} /> : <Music2 size={18} />}
              {generating ? t('create.generating') : t('create.generate_song')}
            </Button>
            <GenerationProgress active={generating} kind="song" lang={lang} estimate={3} />
          </div>
        </div>

        {/* Right: recent creations */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#101012] border border-white/5 rounded-2xl p-4">
            <h3 className="font-semibold mb-3">{lang==='fr'?'Tes dernières créations':'Your recent creations'}</h3>
            {recents.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">
                {lang==='fr'?'Rien encore. Génère ta première chanson 🎛️':'Nothing yet. Generate your first song.'}
              </div>
            ) : (
              <div className="space-y-1">
                {recents.map(s => <SongCard key={s.id} song={s} list={recents} layout="row" />)}
              </div>
            )}
          </div>
          <div className="bg-[#101012] border border-white/5 rounded-2xl p-4 text-sm text-zinc-400">
            <div className="flex items-center gap-2 mb-2 text-zinc-200"><Sparkles size={14} className="text-yellow-300" /> {lang==='fr'?'Astuces':'Tips'}</div>
            <ul className="list-disc list-inside space-y-1">
              <li>{lang==='fr'?'Décris l’instrumentation: 808, percussions afro, riddim...':'Describe instrumentation: 808, afro percussion, riddim...'}</li>
              <li>{lang==='fr'?'Indique le tempo (BPM) et la tonalité':'Specify tempo (BPM) and key'}</li>
              <li>{lang==='fr'?'Mentionne des références (sans noms d’artistes)':'Add references (without artist names)'}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="h-24" />
    </div>
  );
}
